import React, { useState, useEffect, useContext } from 'react';
import { Card, Row, Col, Button } from 'react-bootstrap';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';

const Dashboard = () => {
  const [stats, setStats] = useState({
    projects: 0,
    clients: 0,
    recentProjects: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  
  const { token, user, isAuthenticated } = useContext(AuthContext);
  
  useEffect(() => {
    console.log('Dashboard useEffect - user:', user, 'isAuthenticated:', isAuthenticated);
    
    // If not authenticated, don't do anything
    if (!isAuthenticated) {
      console.log('Not authenticated, waiting...');
      return;
    }
    
    // Check if admin user should be redirected
    if (user && user.isAdmin) {
      console.log('Admin user detected, redirecting...');
      setShouldRedirect(true);
      setIsLoading(false);
      return;
    }
    
    // Don't fetch if no user or still loading user
    if (!user || !token) {
      console.log('No user or token yet, waiting...');
      setIsLoading(false); // Stop showing loading since we're waiting for auth
      return;
    }
    
    // Prevent multiple fetches
    if (dataFetched) {
      return;
    }
    
    const fetchStats = async () => {
      try {
        console.log('Fetching stats for user:', user);
        
        // Regular user flow - Get projects
        const projectsResponse = await fetch('/api/projects?noPagination=true', {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!projectsResponse.ok) {
          const errorData = await projectsResponse.json();
          console.error('Projects API error:', errorData);
          throw new Error(errorData.message || 'Failed to fetch projects');
        }
        
        const projectsData = await projectsResponse.json();
        
        // Get clients
        const clientsResponse = await fetch('/api/customers/clients/all', {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!clientsResponse.ok) {
          throw new Error('Failed to fetch clients');
        }
        
        const clientsData = await clientsResponse.json();
        
        // Sort projects by creation date to get the most recent ones
        const sortedProjects = [...projectsData].sort((a, b) => 
          new Date(b.CreatedAt) - new Date(a.CreatedAt)
        );
        
        setStats({
          projects: projectsData.length,
          clients: clientsData.length,
          recentProjects: sortedProjects.slice(0, 5) // Get top 5 recent projects
        });
        
        setDataFetched(true);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        toast.error(err.message || 'Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (user && !user.isAdmin) {
      fetchStats();
    }
  }, [token, user, isAuthenticated, dataFetched]);
  
  // Handle redirect for admin users
  if (shouldRedirect) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  
  // Show loading while checking authentication
  if (!user && isAuthenticated) {
    return <div className="text-center mt-5">Loading user information...</div>;
  }
  
  if (isLoading) {
    return <div className="text-center mt-5">Loading dashboard...</div>;
  }
  
  // Regular user dashboard
  return (
    <div>
      <h2 className="mb-4">Dashboard</h2>
      
      <Row className="mb-4">
        <Col md={4}>
          <Card className="stats-card primary">
            <Card.Body>
              <Card.Title>Projects</Card.Title>
              <Card.Text className="display-4">{stats.projects}</Card.Text>
              <Button as={Link} to="/projects" variant="outline-primary">View All Projects</Button>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={4}>
          <Card className="stats-card success">
            <Card.Body>
              <Card.Title>Clients</Card.Title>
              <Card.Text className="display-4">{stats.clients}</Card.Text>
              <Button as={Link} to="/clients" variant="outline-success">View All Clients</Button>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={4}>
          <Card className="stats-card warning">
            <Card.Body>
              <Card.Title>Quick Actions</Card.Title>
              <div className="d-grid gap-2">
                <Button as={Link} to="/projects/create" variant="outline-warning" style={{ color: 'black' }}>Create New Project</Button>
                <Button as={Link} to="/clients/create" variant="outline-warning" style={{ color: 'black' }}>Add New Client</Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <Card>
        <Card.Header>
          <h5 className="mb-0">Recent Projects</h5>
        </Card.Header>
        <Card.Body>
          {stats.recentProjects.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Project Name</th>
                    <th>Client</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentProjects.map(project => (
                    <tr key={project.ProjectId}>
                      <td>{project.ProjectName}</td>
                      <td>{project.ClientName}</td>
                      <td>
                        <span 
                          className="badge"
                          style={{
                            backgroundColor: 
                              project.Status === 'New' ? '#007bff' :
                              project.Status === 'Proposal Sent' ? '#fd7e14' :
                              project.Status === 'Invoice Sent' ? '#8B5CF6' :
                              project.Status === 'In Progress' ? '#6c757d' :
                              project.Status === 'Completed' ? '#28a745' :
                              project.Status === 'On Hold' ? '#dc3545' :
                              '#6c757d',
                            color: '#fff',
                            border: 'none',
                            fontSize: '0.875rem',
                            padding: '0.375rem 0.75rem'
                          }}
                        >
                          {project.Status}
                        </span>
                      </td>
                      <td>{new Date(project.CreatedAt).toLocaleDateString()}</td>
                      <td>
                        <Button 
                          as={Link} 
                          to={`/projects/${project.ProjectId}`}
                          variant="primary"
                          size="sm"
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center">No projects found. <Link to="/projects/create">Create your first project</Link>.</p>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default Dashboard;
